import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';

declare var mixpanel: any;

@Injectable({
  providedIn: 'root'
})
export class ProductAnalyticService {
  
  private identifiedUserId: string | null = null;
  private initializationAttempted: boolean = false;
  private isInitializing: boolean = false;
  private isInIframe: boolean = false;
  private useParentMixpanel: boolean = false;

  constructor() {
    this.detectIframeContext();
  }

  /**
   * Detect if application is running in an iframe
   */
  private detectIframeContext(): void {
    try {
      this.isInIframe = window.self !== window.top;
    } catch (error) {
      // Cross-origin iframe - assume we're in iframe
      this.isInIframe = true;
    }
  }

  /**
   * Main tracking method - call this to track events
   * @param eventName - Name of the event to track
   * @param queryParams - Query parameters containing user and context data
   * @param eventSpecificData - Additional event-specific properties (optional)
   * @param componentState - Component state data (optional)
   */
  mixpanelTracking(
    eventName: string, 
    queryParams: any = {}, 
    eventSpecificData: any = {},
    componentState: any = {}
  ): void {
    // Skip tracking if no userId in queryParams
    if (!queryParams.contactId) {
      return;
    }

    // If in iframe and Mixpanel not available, fall back to postMessage
    if (this.isInIframe && !this.isMixpanelInitialized() && this.initializationAttempted) {
      this.sendTrackingToParent(eventName, queryParams, eventSpecificData, componentState);
      return;
    }

    // Check if mixpanel is available, with retry mechanism
    if (!this.isMixpanelInitialized()) {
      // If not initialized yet, try to initialize and retry tracking after a delay
      if (!this.initializationAttempted) {
        this.attemptInitialization();
        // Retry tracking after initialization attempt
        setTimeout(() => {
          if (this.isMixpanelInitialized()) {
            this.executeTracking(eventName, queryParams, eventSpecificData, componentState);
          } else if (this.isInIframe) {
            // Fallback to postMessage if still not initialized in iframe
            this.sendTrackingToParent(eventName, queryParams, eventSpecificData, componentState);
          }
        }, 1000);
      } else if (this.isInIframe) {
        // Already attempted initialization, use postMessage fallback
        this.sendTrackingToParent(eventName, queryParams, eventSpecificData, componentState);
      }
      return;
    }

    this.executeTracking(eventName, queryParams, eventSpecificData, componentState);
  }

  /**
   * Fallback: Send tracking data to parent window via postMessage
   * This is used when Mixpanel cannot be initialized in iframe context
   */
  private sendTrackingToParent(
    eventName: string,
    queryParams: any,
    eventSpecificData: any,
    componentState: any
  ): void {
    if (!window.parent || window.parent === window.self) {
      return;
    }

    try {
      const userId = queryParams.userId || queryParams.contactId || '';
      let loggedInUserId = String(userId);
      if (!environment.production) {
        const envName = environment.environmentName || 'dev';
        loggedInUserId = `${envName.toUpperCase()}_${loggedInUserId}`;
      }

      const eventData: any = {
        isQuoteAssistTracking: true,
        ...queryParams,
        ...eventSpecificData,
        ...componentState,
        eventName: eventName,
        event: eventName,
        user_id: loggedInUserId,
        distinct_id: loggedInUserId,
        "$user_id": loggedInUserId,
        "$distinct_id": loggedInUserId
      };

      if (!environment.production) {
        eventData.env = environment.environmentName || 'dev';
      }

      window.parent.postMessage(eventData, '*');
    } catch (error) {
      // Silent failure for postMessage
    }
  }

  /**
   * Execute the actual tracking logic
   */
  private executeTracking(
    eventName: string,
    queryParams: any,
    eventSpecificData: any,
    componentState: any
  ): void {

    // Get user ID from queryParams
    const userId = queryParams.userId || queryParams.contactId || '';
    if (!userId) {
      return;
    }

    // Prepare user ID with environment prefix for non-production
    let loggedInUserId = String(userId);
    if (!environment.production) {
      const envName = environment.environmentName || 'dev';
      loggedInUserId = `${envName.toUpperCase()}_${loggedInUserId}`;
    }
    // Identify user if not already identified or if user changed
    if (this.identifiedUserId !== loggedInUserId) {
      if (this.isMixpanelInitialized()) {
        const mp = this.getMixpanelInstance();
        if (mp) {
          mp.identify(loggedInUserId);
          // Build people properties, checking multiple sources for email
          const email = this.getValidStringValue(queryParams.email || queryParams.emailId);
          const phone = this.getValidStringValue(queryParams.phone);
          const firstName = this.getValidStringValue(queryParams.firstName);
          const lastName = this.getValidStringValue(queryParams.lastName);

          const peopleProperties: any = {
            "$user_id": loggedInUserId,
            "$distinct_id": loggedInUserId
          };

          // Only set properties if they have valid non-empty values
          if (email) {
            peopleProperties["$email"] = email;
          }
          if (phone) {
            peopleProperties["$phone"] = phone;
          }
          if (firstName) {
            peopleProperties["$first_name"] = firstName;
          }
          if (lastName) {
            peopleProperties["$last_name"] = lastName;
          }

          mp.people.set(peopleProperties);
        }
      }
      this.identifiedUserId = loggedInUserId;
    }

    // Merge all data
    const eventData: any = {
      // All queryParams included
      ...queryParams,
      
      // Event-specific data
      ...eventSpecificData,
      
      // Component state
      ...componentState,
      
      // Standard properties
      event: eventName,
      user_id: loggedInUserId,
      distinct_id: loggedInUserId,
      "$user_id": loggedInUserId,
      "$distinct_id": loggedInUserId
    };

    // Add environment info for non-production
    if (!environment.production) {
      eventData.env = environment.environmentName || 'dev';
    }

    // Track the event
    this.mixpanelTrackEvent(eventName, eventData);
  }

  /**
   * Get a valid string value, returning null if empty or invalid
   * This prevents setting empty strings in Mixpanel
   */
  private getValidStringValue(value: any): string | null {
    if (!value) {
      return null;
    }
    
    if (typeof value !== 'string') {
      return null;
    }
    
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  /**
   * Get the appropriate Mixpanel instance (current window or parent window)
   */
  private getMixpanelInstance(): any {
    if (this.useParentMixpanel) {
      try {
        if (window.parent && window.parent !== window.self) {
          return (window.parent as any).mixpanel;
        }
      } catch (error) {
        // Cross-origin - cannot access parent window
        this.useParentMixpanel = false;
      }
    }
    return mixpanel;
  }

  /**
   * Track custom event with retry logic
   */
  private mixpanelTrackEvent(eventName: string, data: any = {}, retryCount = 0): void {
    if (this.isMixpanelInitialized()) {
      try {
        const mp = this.getMixpanelInstance();
        if (mp) {
          mp.track(eventName, data);
        }
      } catch (error) {
        // Silent failure - no console logging
      }
    } else if (retryCount < 3) {
      setTimeout(() => {
        this.mixpanelTrackEvent(eventName, data, retryCount + 1);
      }, 1000);
    }
  }

  /**
   * Check if Mixpanel is initialized and ready
   * In iframe context, also checks parent window's Mixpanel if same-origin
   */
  isMixpanelInitialized(): boolean {
    // If using parent window's Mixpanel, check that
    if (this.useParentMixpanel) {
      try {
        if (window.parent && window.parent !== window.self) {
          const parentMixpanel = (window.parent as any).mixpanel;
          if (parentMixpanel && typeof parentMixpanel.track === 'function') {
            return true;
          }
        }
      } catch (error) {
        // Cross-origin - cannot access parent window
        this.useParentMixpanel = false;
      }
    }

    // Check if mixpanel object exists in current window
    if (typeof mixpanel === 'undefined' || mixpanel === null) {
      return false;
    }
    
    // Check if mixpanel is fully loaded (not just the stub)
    // The stub has __SV property, but track function only exists after full load
    if (typeof mixpanel.track !== 'function') {
      // Try to initialize if not already attempted
      if (!this.initializationAttempted && !this.isInitializing) {
        this.attemptInitialization();
      }
      return false;
    }
    
    return true;
  }

  /**
   * Attempt to initialize Mixpanel if not already initialized
   * Uses retry mechanism to wait for library to fully load
   * In iframe context, tries to use parent window's Mixpanel first
   */
  private attemptInitialization(retryCount: number = 0): void {
    if (this.isInitializing) {
      return;
    }

    // If in iframe, try to use parent window's Mixpanel first (same-origin only)
    if (this.isInIframe && !this.useParentMixpanel && retryCount === 0) {
      try {
        if (window.parent && window.parent !== window.self) {
          const parentMixpanel = (window.parent as any).mixpanel;
          if (parentMixpanel && typeof parentMixpanel.track === 'function') {
            this.useParentMixpanel = true;
            this.initializationAttempted = true;
            this.isInitializing = false;
            return;
          }
        }
      } catch (error) {
        // Cross-origin - cannot access parent window, continue with iframe initialization
      }
    }

    const maxRetries = 20; // Wait up to 10 seconds (20 * 500ms)
    
    // Check if mixpanel object exists
    if (typeof mixpanel === 'undefined' || mixpanel === null) {
      if (retryCount < maxRetries) {
        this.isInitializing = true;
        setTimeout(() => {
          this.isInitializing = false;
          this.attemptInitialization(retryCount + 1);
        }, 500);
      } else {
        this.initializationAttempted = true;
      }
      return;
    }

    // Get token from environment
    const token = (environment as any).mixpanel?.token;
    if (!token) {
      this.initializationAttempted = true;
      return;
    }

    // Check if mixpanel.init is available (library fully loaded)
    if (typeof mixpanel.init !== 'function') {
      if (retryCount < maxRetries) {
        this.isInitializing = true;
        setTimeout(() => {
          this.isInitializing = false;
          this.attemptInitialization(retryCount + 1);
        }, 500);
      } else {
        this.initializationAttempted = true;
      }
      return;
    }

    // Initialize Mixpanel
    // In iframe context, use cookie-based persistence instead of localStorage
    try {
      const initOptions: any = {
        track_pageview: true,
        ignore_dnt: true
      };

      // Use cookie persistence in iframe to avoid localStorage restrictions
      if (this.isInIframe) {
        initOptions.persistence = 'cookie';
      } else {
        initOptions.persistence = 'localStorage';
      }

      mixpanel.init(token, initOptions);
      this.initializationAttempted = true;
      this.isInitializing = false;
    } catch (error) {
      // Silent failure
      this.initializationAttempted = true;
      this.isInitializing = false;
    }
  }

  /**
   * Reset Mixpanel session (e.g., on logout/navigation)
   */
  resetSession(): void {
    if (this.isMixpanelInitialized()) {
      try {
        const mp = this.getMixpanelInstance();
        if (mp?.reset) {
          mp.reset();
        }
      } catch (error) {
        // Silent failure
      }
    }
    this.identifiedUserId = null;
  }
}

